import { useMemo, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import {
  exportLeadhoopLeadsCsv,
  getLeadhoopAccounts,
  searchLeadhoopLeads,
  type LeadhoopLeadSearchRequest,
  type LeadhoopSortKey,
} from "@/api/leadhoop";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import { Spinner } from "@/components/Spinner";
import { fmtDate, fmtMoney } from "@/lib/format";

const SORT_OPTIONS: { value: LeadhoopSortKey; label: string }[] = [
  { value: "create_date_desc", label: "Newest first" },
  { value: "create_date_asc", label: "Oldest first" },
  { value: "status_asc", label: "Status A→Z" },
  { value: "revenue_desc", label: "Revenue (high → low)" },
  { value: "revenue_asc", label: "Revenue (low → high)" },
];

const PAGE_SIZE = 25;
const DEFAULT_SORT: LeadhoopSortKey = "create_date_desc";

function readFilter(params: URLSearchParams): LeadhoopLeadSearchRequest {
  const num = (k: string): number | undefined => {
    const v = params.get(k);
    if (v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    account_slug: params.get("account_slug") || undefined,
    publisher_session_id: num("publisher_session_id"),
    status: params.get("status") || undefined,
    create_date_from: params.get("create_date_from") || undefined,
    create_date_to: params.get("create_date_to") || undefined,
    q: params.get("q") || undefined,
    sort: (params.get("sort") as LeadhoopSortKey) || DEFAULT_SORT,
    page: num("page") ?? 1,
    page_size: PAGE_SIZE,
  };
}

export function LeadhoopLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(() => readFilter(searchParams), [searchParams]);

  const [form, setForm] = useState<LeadhoopLeadSearchRequest>(filter);
  const [exportError, setExportError] = useState<unknown>(null);
  const [exporting, setExporting] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ["leadhoop", "accounts"],
    queryFn: getLeadhoopAccounts,
  });

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["leadhoop", "leads", filter],
    queryFn: () => searchLeadhoopLeads(filter),
    placeholderData: (prev) => prev,
  });

  function commit(next: LeadhoopLeadSearchRequest) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === "" || v === null) continue;
      if (k === "page_size") continue;
      params.set(k, String(v));
    }
    setSearchParams(params);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    commit({ ...form, page: 1 });
  }

  function reset() {
    const empty: LeadhoopLeadSearchRequest = {
      sort: DEFAULT_SORT,
      page: 1,
    };
    setForm(empty);
    commit(empty);
  }

  async function onExport() {
    setExportError(null);
    setExporting(true);
    try {
      await exportLeadhoopLeadsCsv(filter);
    } catch (err) {
      setExportError(err);
    } finally {
      setExporting(false);
    }
  }

  const sessionOptions: number[] = useMemo(() => {
    if (!form.account_slug || !accounts) return [];
    return (
      accounts.find((a) => a.slug === form.account_slug)?.publisher_session_ids ??
      []
    );
  }, [accounts, form.account_slug]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="LeadHoop leads"
        subtitle="Search, filter and export leads scraped from LeadHoop"
        actions={
          <button
            type="button"
            onClick={onExport}
            className="btn-secondary"
            disabled={exporting}
          >
            {exporting && <Spinner size="sm" />}
            Export CSV
          </button>
        }
      />

      <form onSubmit={onSubmit} className="card">
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div>
            <label className="label">Account</label>
            <select
              className="input"
              value={form.account_slug ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  account_slug: e.target.value || undefined,
                  publisher_session_id: undefined,
                })
              }
            >
              <option value="">All accounts</option>
              {accounts?.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.slug}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Publisher session</label>
            <select
              className="input"
              value={form.publisher_session_id ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  publisher_session_id: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
              disabled={!form.account_slug}
            >
              <option value="">All sessions</option>
              {sessionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <input
              className="input"
              value={form.status ?? ""}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value || undefined })
              }
              placeholder="Accepted"
            />
          </div>
          <div>
            <label className="label">Sort by</label>
            <select
              className="input"
              value={form.sort ?? DEFAULT_SORT}
              onChange={(e) =>
                setForm({
                  ...form,
                  sort: e.target.value as LeadhoopSortKey,
                })
              }
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Search</label>
            <input
              className="input"
              type="search"
              placeholder="email, phone, name, record id, consumer id"
              value={form.q ?? ""}
              onChange={(e) => setForm({ ...form, q: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Created from</label>
            <input
              className="input"
              type="date"
              value={form.create_date_from ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  create_date_from: e.target.value || undefined,
                })
              }
            />
          </div>
          <div>
            <label className="label">Created to</label>
            <input
              className="input"
              type="date"
              value={form.create_date_to ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  create_date_to: e.target.value || undefined,
                })
              }
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button type="button" onClick={reset} className="btn-secondary">
            Reset
          </button>
          <button type="submit" className="btn-primary">
            Apply filters
          </button>
        </div>
      </form>

      {error && <ErrorBanner error={error} />}
      {exportError !== null && (
        <ErrorBanner
          error={exportError}
          onDismiss={() => setExportError(null)}
        />
      )}

      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Record</th>
                  <th>Created</th>
                  <th>Account</th>
                  <th>Session</th>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>State</th>
                  <th>Offer</th>
                  <th className="text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td
                      colSpan={11}
                      className="p-6 text-center text-slate-500"
                    >
                      <Spinner /> Loading…
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  data?.items.map((lead) => (
                    <tr key={`${lead.account_slug}-${lead.record_id}`}>
                      <td>
                        <Link
                          to={`/leadhoop/leads/${encodeURIComponent(lead.account_slug)}/${lead.record_id}`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {lead.record_id}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap text-slate-600">
                        {fmtDate(lead.create_date)}
                      </td>
                      <td>{lead.account_slug}</td>
                      <td>{lead.publisher_session_id}</td>
                      <td>{lead.status ?? "—"}</td>
                      <td>
                        {[lead.first_name, lead.last_name]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </td>
                      <td className="truncate text-slate-700">
                        {lead.email ?? "—"}
                      </td>
                      <td className="text-slate-700">
                        {lead.phone_1 ?? "—"}
                      </td>
                      <td className="text-slate-700">{lead.state ?? "—"}</td>
                      <td className="text-slate-600">{lead.offer ?? "—"}</td>
                      <td className="text-right tabular-nums text-slate-700">
                        {fmtMoney(lead.revenue)}
                      </td>
                    </tr>
                  ))}
                {!isLoading && data?.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={11}
                      className="p-6 text-center text-slate-500"
                    >
                      No leads match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {data && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination
              page={data.page}
              pages={Math.max(
                1,
                Math.ceil(data.total / Math.max(1, data.page_size)),
              )}
              total={data.total}
              onChange={(p) => commit({ ...filter, page: p })}
            />
          </div>
        )}
      </div>

      {isFetching && !isLoading && (
        <div className="text-xs text-slate-400">Refreshing…</div>
      )}
    </div>
  );
}
